import { Box } from "@chakra-ui/react";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAccount, useSwitchChain } from "wagmi";
// import BaseImage from "@components/BaseImage";

import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  getLSTOptionsForChain,
  toFixedWithoutRounding,
} from "@constants/common";
import useWithdrawalManagerPaused from "@hooks/useWithdrawalManagerPaused";
import RecoveryNotice from "@components/RecoveryNotice";
import { EVENTS } from "@constants/reduxEvents";

// import LstTokenSelect from "@components/LstTokenSelect";
// import TicketInput from "@components/TicketInput";
// import TicketInputRightSection from "@components/TicketInputRightSection";

import { ETH, STETH } from "@config/contractsConfig";
import {
  APPROVE_UNSTAKING_CLICK,
  CONNECT_WALLET_CLICK,
  UNSTAKE_CTA,
  UNSTAKE_TYPE_SWITCHED,
} from "@constants/analytics";
import useDappConfig from "@hooks/useDappConfig";
import { ERDataType } from "@hooks/useExchangeRate";
import useTokenBalance from "@hooks/useTokenBalance";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import {
  emitReduxEvent,
  formatNumber,
  isAmountInput,
  numberToCurrency,
  trackEvent,
} from "@lib/utils";

import useDappChain from "@hooks/useDappChain";
import useUnstakeEstimate from "@hooks/useUnstakeEstimate";
import useUnstakeInfo from "@hooks/useUnstakeInfo";
import { updateUserData } from "@store";

import useAllowance from "@hooks/useAllowance";
import { Button, Typography } from "@kelp-dao/kernel-design-system";
import { WarningIcon } from "@phosphor-icons/react";
import TokenInput from "./TokenInput";
import { EUnstakeMode, TUnstakeMode } from "./restake/types";
import TransactionSummary from "./restake/TransactionSummary";
import ModeOption from "./restake/ModeOption";

const MAX_FRACTION_DIGITS = 4;

interface StakeComponentProps {
  conversionData: ERDataType;
  isActive: boolean;
}

const UnstakeComponent = ({
  conversionData,
  isActive,
}: StakeComponentProps) => {
  const [unstakeAmount, setUnstakeAmount] = useState("");
  const [error, setError] = useState("");

  const { selectedERC20 } = useSelector((state: any) => state.user);

  const isETHWithdrawal = selectedERC20 === ETH;

  const conversions = useMemo(() => {
    const conversionToERC20 = conversionData?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionData?.exchangeRate]);

  const { switchChain } = useSwitchChain();

  const { xTokenAllowance } = useAllowance();

  const { isConnected, address } = useAccount();

  const { xTokenTokenBalance, tokensBalances, nativeTokenBalance } =
    useTokenBalance();

  const { openConnectModal } = useConnectModal();

  const { config, isWithdrawSupported, withdrawEnabledChains } =
    useDappConfig();

  const { isPaused: isWithdrawalManagerPaused } = useWithdrawalManagerPaused();

  const { returns } = useUnstakeEstimate(unstakeAmount);

  const {
    minAmountToWithdraw,
    bufferPoolForAsset,
    withdrawalableAmount,
    instantWithdrawalAvailableInAsset,
    isInstantWithdrawalEnabled,
    isInstantWithdrawalPending,
    instantWithdrawalFee,
  } = useUnstakeInfo();

  const { chain } = useDappChain();

  // Start with undefined to avoid glitch; we'll set mode once data loads
  const [selectedMode, setSelectedMode] = useState<TUnstakeMode | undefined>(
    undefined,
  );
  const [hasManuallySelected, setHasManuallySelected] = useState(false);

  // Use external mode if provided, otherwise use internal state
  const handleModeChange = useCallback((mode: TUnstakeMode) => {
    setSelectedMode(mode);
    setHasManuallySelected(true);
    trackEvent(UNSTAKE_TYPE_SWITCHED, { mode });
  }, []);

  // Set initial mode once data is loaded, then react to changes
  useEffect(() => {
    // Wait until data is loaded
    if (isInstantWithdrawalPending) return;

    // Initial load: set mode based on isInstantWithdrawalEnabled
    if (selectedMode === undefined) {
      setSelectedMode(
        isInstantWithdrawalEnabled
          ? EUnstakeMode.INSTANT
          : EUnstakeMode.STANDARD,
      );
      return;
    }

    // Only auto-select if user hasn't manually chosen
    if (!hasManuallySelected && isInstantWithdrawalEnabled) {
      setSelectedMode(EUnstakeMode.INSTANT);
    } else if (
      !isInstantWithdrawalEnabled &&
      selectedMode === EUnstakeMode.INSTANT
    ) {
      // Force to standard if instant becomes unavailable (regardless of manual selection)
      setSelectedMode(EUnstakeMode.STANDARD);
      setHasManuallySelected(false);
    }
  }, [
    isInstantWithdrawalEnabled,
    isInstantWithdrawalPending,
    hasManuallySelected,
    selectedMode,
  ]);

  const checkInput = useCallback(
    (value = unstakeAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > xTokenTokenBalance) {
        error = `You can't unstake more than your available rsETH balance.`;
      } else if (Number(value) < Number(minAmountToWithdraw)) {
        error = `Minimum rsETH withdrawal amount is ${minAmountToWithdraw}`;
      } else if (
        selectedMode === EUnstakeMode.INSTANT &&
        bufferPoolForAsset > 0 &&
        Number(value) > bufferPoolForAsset
      ) {
        error = `Maximum instant withdrawal is ${numberToCurrency(
          bufferPoolForAsset?.toFixed(4) || "0",
        )} rsETH`;
      }
      return error;
    },
    [
      isConnected,
      minAmountToWithdraw,
      xTokenTokenBalance,
      selectedMode,
      bufferPoolForAsset,
      unstakeAmount,
    ],
  );

  const tokenAmount =
    selectedERC20 === ETH
      ? nativeTokenBalance
      : tokensBalances[selectedERC20] || 0;

  useEffect(() => {
    if (unstakeAmount !== "") {
      const error = checkInput(unstakeAmount);
      setError(error);
    }
  }, [address, unstakeAmount, checkInput]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (!isWithdrawSupported) {
      dispatch(
        updateUserData({
          selectedERC20: ETH,
        }),
      );
      return;
    }

    const priorityToken = getLSTOptionsForChain(chain.id, true).sort(
      (a: any, b: any) => (a?.unstakePriority || 0) - (b?.unstakePriority || 0),
    )[0];

    if (!priorityToken) {
      dispatch(
        updateUserData({
          selectedERC20: STETH,
        }),
      );
      return;
    }

    dispatch(
      updateUserData({
        selectedERC20: priorityToken!.key,
      }),
    );
  }, [config.tokens, dispatch, isActive, chain.id, isWithdrawSupported]);

  const isApproved = useMemo(() => {
    const amount = unstakeAmount || "";
    return (
      xTokenAllowance &&
      Number(xTokenAllowance) &&
      Number(xTokenAllowance) >= Number(amount)
    );
  }, [xTokenAllowance, unstakeAmount]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isAmountInput(val) || val === "") {
      if (val.startsWith("00") && !val.startsWith("00.")) {
        return;
      }

      if (val.indexOf(".") === -1) {
        setUnstakeAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setUnstakeAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else {
      if (!isWithdrawSupported) {
        return `Switch to ${withdrawEnabledChains[0].name} to Unstake`;
      }
      if (isApproved) {
        return selectedMode === EUnstakeMode.INSTANT
          ? "Request instant unstaking"
          : "Request standard unstaking";
      }
      return APPROVE_CTA;
    }
  };

  const getBannerText = useMemo(() => {
    if (!isWithdrawSupported) {
      return "Unstake and withdrawals are available on Ethereum. Bridge your assets to Ethereum chain and initiate unstaking";
    } else {
      let msgStr = "";
      let daysText = "";

      if (selectedMode === EUnstakeMode.STANDARD) {
        daysText = "15-21";
        if (isETHWithdrawal) {
          msgStr = `Unstake requests usually takes ${daysText} days due to the Ethereum network's exit queue and EigenLayer delays (14 days).`;
        } else {
          const extraText =
            " If funds are available in the withdrawal pool, your request maybe processed in 2-3 days.";
          msgStr = `Unstake requests usually takes ${daysText} days due to EigenLayer delays (14 days).${extraText}`;
        }
      }
      return msgStr;
    }
  }, [isWithdrawSupported, selectedMode, isETHWithdrawal]);

  const handleClick = useCallback(() => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET_CLICK, {
        cta_location: "center_wiget",
        cta: CONNECT_WALLET_CTA,
      });
    } else {
      if (!isWithdrawSupported) {
        switchChain({ chainId: withdrawEnabledChains[0].id });
      } else if (isApproved) {
        if (!unstakeAmount) {
          setError(
            "This field can't be blank or zero. Please provide valid input.",
          );
          return;
        }
        trackEvent(UNSTAKE_CTA, {
          amount: unstakeAmount,
          mode: selectedMode,
        });

        // emitReduxEvent(EVENTS.HANDLE_UNSTAKE, { amount: unstakeAmount });
        emitReduxEvent(EVENTS.HANDLE_UNSTAKE_GAIN_OPP, {
          nextAction: EVENTS.HANDLE_UNSTAKE,
          amount: unstakeAmount,
          mode: selectedMode,
        });
        /* isBufferSufficient:
            bufferPoolFor > 0 ? bufferPool > Number(unstakeAmount) : true, */
        setUnstakeAmount("");
      } else {
        // emitReduxEvent(EVENTS.HANDLE_XTOKEN_APPROVE, { amount: unstakeAmount });
        emitReduxEvent(EVENTS.HANDLE_UNSTAKE_GAIN_OPP, {
          nextAction: EVENTS.HANDLE_XTOKEN_APPROVE,
          amount: unstakeAmount,
          mode: selectedMode,
        });
        trackEvent(APPROVE_UNSTAKING_CLICK, {
          asset: selectedERC20,
          mode: selectedMode,
        });
      }
    }
  }, [
    isConnected,
    openConnectModal,
    isWithdrawSupported,
    isApproved,
    switchChain,
    withdrawEnabledChains,
    unstakeAmount,
    selectedERC20,
    selectedMode,
  ]);

  const handleMaxClick = useCallback(() => {
    setUnstakeAmount(toFixedWithoutRounding(String(xTokenTokenBalance), 4));
  }, [xTokenTokenBalance]);

  const formattedReturns = useMemo(() => {
    const actualReturns = Number(returns) || 0;
    return selectedMode === EUnstakeMode.INSTANT
      ? actualReturns - (actualReturns * instantWithdrawalFee) / 100
      : actualReturns;
  }, [instantWithdrawalFee, returns, selectedMode]);

  return (
    <Box pos="relative">
      <Box
        pos="relative"
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        color="textSecondary"
        pointerEvents={isWithdrawSupported || !address ? "all" : "none"}
        filter="auto"
        blur={isWithdrawSupported || !address ? "0" : "3px"}
      >
        {/* <LstTokenSelect
          hideTags
          title="Withdraw rsETH as"
          forUnstake
          rightSection={
            <>
              <Text>Fast withdrawal</Text>
            </>
          }
        />
        <Flex w="100%" alignItems="center">
          <Flex
            justifyContent="space-between"
            flexGrow={1}
            flexDir={{ base: "column", xl: "row" }}
          >
            <Box
              display={{ base: isConnected ? "none" : "inherit", xl: "block" }}
              h="20px"
            >
              <Text>Enter amount of rsETH</Text>
            </Box>
          </Flex>
        </Flex> */}

        {/* <TicketInput
          inputRightElement={
            <TicketInputRightSection
              isConnected={isConnected}
              tokenAmount={xTokenTokenBalance}
              onClick={handleMaxClick}
              token={config.xtoken.symbol}
            />
          }
          value={unstakeAmount}
          onChange={(value: string) => handleChange(value)}
          error={error}
        /> */}

        <TokenInput
          hideTags
          title="Withdraw rsETH as"
          forUnstake
          value={unstakeAmount}
          onChange={(value: string) => handleChange(value)}
          error={error}
          token={config.xtoken.symbol}
          onMaxClick={handleMaxClick}
          tokenBalance={
            tokenAmount
              ? formatNumber(Number(tokenAmount), false, 4, true)
              : "-"
          }
          balance={
            xTokenTokenBalance
              ? formatNumber(Number(xTokenTokenBalance), false, 4, true)
              : "-"
          }
          showBalance={isConnected}
          selectedMode={selectedMode}
          handleModeChange={handleModeChange}
          explicitLabel="rsETH"
          explicitBalance={
            xTokenTokenBalance
              ? formatNumber(Number(xTokenTokenBalance), false, 4, true)
              : "-"
          }
        />

        <TransactionSummary
          items={[
            {
              label: "You will get",
              value: `${
                formatNumber(
                  Number(formattedReturns),
                  false,
                  MAX_FRACTION_DIGITS,
                  true,
                ) || 0
              } ${selectedERC20}`,
            },
            {
              label: "Exchange rate",
              value: conversions.toERC20
                ? `1 ${config.xtoken.symbol} = ${toFixedWithoutRounding(
                    conversions.toERC20.toString(),
                    4,
                  )} ${selectedERC20}`
                : "",
              condition: !!conversions.toERC20,
            },
            {
              label: `Total available ${selectedERC20}`,
              value: `${numberToCurrency(
                selectedMode === EUnstakeMode.INSTANT
                  ? instantWithdrawalAvailableInAsset?.toFixed(4) || "0"
                  : withdrawalableAmount?.toString() || "0",
              )} ${selectedERC20}`,
              condition: !!(
                isWithdrawSupported &&
                (selectedMode === EUnstakeMode.INSTANT
                  ? instantWithdrawalAvailableInAsset
                  : withdrawalableAmount)
              ),
              tooltip: (
                <Typography
                  type="body"
                  variant="small"
                  className="text-text-secondary"
                >
                  {selectedMode === EUnstakeMode.INSTANT
                    ? "This is the total amount available for instant withdrawal"
                    : "This is the total amount of asset available with Kelp that can be withdrawn"}
                </Typography>
              ),
            },
            // {
            //   label: `Available for instant withdrawal`,
            //   value: `${numberToCurrency(
            //     bufferPoolForAsset?.toString() || "0"
            //   )} ${selectedERC20}`,
            //   condition: !!isWithdrawSupported,
            // tooltip: (
            //   <Typography
            //     type="body"
            //     variant="small"
            //     className="text-text-secondary"
            //   >
            //     This is the amount available in the buffer pool for instant
            //     withdrawals
            //   </Typography>
            // ),
            // },
          ]}
        />

        {/* Mode Options - Instant/Standard */}
        {!isWithdrawalManagerPaused && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            {selectedMode === undefined ? (
              <>
                {/* Skeleton loading state */}
                <div className="h-[72px] rounded-lg bg-gray-200/10 animate-pulse" />
                <div className="h-[72px] rounded-lg bg-gray-200/10 animate-pulse" />
              </>
            ) : (
              <>
                <ModeOption
                  mode={EUnstakeMode.INSTANT}
                  isActive={selectedMode === EUnstakeMode.INSTANT}
                  onClick={() => handleModeChange(EUnstakeMode.INSTANT)}
                  disabled={
                    !isInstantWithdrawalEnabled ||
                    !bufferPoolForAsset ||
                    bufferPoolForAsset <= 0
                  }
                  instantWithdrawalFee={instantWithdrawalFee}
                />
                <ModeOption
                  mode={EUnstakeMode.STANDARD}
                  isActive={selectedMode === EUnstakeMode.STANDARD}
                  onClick={() => handleModeChange(EUnstakeMode.STANDARD)}
                  disabled={isWithdrawalManagerPaused}
                />
              </>
            )}
          </div>
        )}
      </Box>
      <RecoveryNotice className="mt-6" />
      <Button
        variant="primary"
        className="w-full py-3 mt-2"
        onClick={handleClick}
        disabled={
          !isWithdrawSupported
            ? isWithdrawSupported
            : !!error || !unstakeAmount || isWithdrawalManagerPaused
        }
      >
        {getButtonText()}
      </Button>
      {selectedMode !== undefined && selectedMode === EUnstakeMode.STANDARD && (
        <div className="flex items-start gap-1 mt-3">
          <div className="flex items-center justify-center mt-0.5">
            <WarningIcon
              color="#FF6B6B"
              weight="regular"
              className="object-contain h-3 w-auto"
            />
          </div>
          <Typography
            type="body"
            variant="xsmall"
            className="text-text-secondary"
          >
            {getBannerText}
          </Typography>
        </div>
      )}
    </Box>
  );
};

export default UnstakeComponent;
